'use client'

import React from 'react'
import { Cog } from 'lucide-react'
import { cn } from '@/lib/utils'
import FooterV2 from '@/components/footer-v2'
import FeeSelector from '@/components/fee-selector'
import BitcoinIcon from '@/icons/bitcoin-filled'
import useFeeEstimates from '@/utils/hooks/useFeeEstimates'
import { DIESEL_ID } from '../lib/amm'
import { formatUsdPrice } from '../lib/format'
import { useAlkanes } from '../lib/store'
import { Change } from './asset-row'
import { TokenIcon } from './token-select'

/// The network-fee readout + the gear that changes it (phone strip only —
/// the desktop bar uses the site footer's own instance). The gear opens the
/// SITE'S fee selector — the same slow/medium/fast preference every builder
/// on the site reads — so the rate chosen here is the `fee_rate` the swap,
/// wrap and unwrap requests carry. `.alkanes-ink` keeps the portaled panel
/// on the app's palette.
function FeeCluster() {
	const { feeRate, isLoading } = useFeeEstimates()
	// "1 sat/vB", "2 sats/vB" — an idle chain sits at 1 all day, so the
	// singular is the common case, not a corner.
	const unit = Number(feeRate) === 1 ? 'sat' : 'sats'
	return (
		<FeeSelector align="end" contentClassName="alkanes-ink">
			<button
				type="button"
				data-testid="alkanes-fee-cluster"
				aria-label={`Network fee ${feeRate || '—'} ${unit} per vB — change speed`}
				className="alkanes-touch flex items-center gap-1.5 whitespace-nowrap rounded-md tabular-nums text-muted-foreground transition-colors hover:text-foreground"
			>
				{isLoading || !feeRate ? '—' : feeRate} {unit}/vB
				<Cog className="size-3.5" aria-hidden="true" />
			</button>
		</FeeSelector>
	)
}

function HealthDot() {
	const health = useAlkanes((s) => s.health)
	const label = health === 'ok' ? 'Stable' : health === 'stale' ? 'Degraded' : 'Down'
	// Dot + accessible name only: the 375px strip now carries two prices and
	// the fee cluster, and the spelled-out label was the collision.
	return (
		<span className="flex items-center" title={label}>
			<span
				aria-hidden="true"
				className={cn(
					'size-1.5 rounded-full',
					health === 'ok' && 'bg-success',
					health === 'stale' && 'bg-warning',
					health === 'down' && 'bg-destructive'
				)}
			/>
			<span className="sr-only">Market data {label.toLowerCase()}</span>
		</span>
	)
}

/// DIESEL beside BTC, in the footer's own grammar (icon, then the figure) —
/// plus the 24h change, colored by the status ramp. Renders nothing until a
/// real price exists; a footer must never invent one.
function DieselPrice({ compact }: { compact?: boolean }) {
	const assets = useAlkanes((s) => s.assets)
	const diesel = React.useMemo(() => assets.find((a) => a.id === DIESEL_ID) ?? null, [assets])
	if (!diesel || diesel.price_usd === null) {
		return null
	}
	return (
		<div
			data-testid="alkanes-footer-diesel"
			className={cn(
				'flex select-none items-center gap-1 tabular-nums text-foreground',
				compact ? 'text-xxs' : 'text-xs'
			)}
		>
			<TokenIcon
				token={{ id: diesel.id, name: diesel.name, icon: diesel.icon }}
				size={compact ? 14 : 16}
			/>
			{formatUsdPrice(diesel.price_usd)}
			<Change v={diesel.change_24h_pct} className="text-xxs" />
		</div>
	)
}

function BtcPriceCompact() {
	const btcUsd = useAlkanes((s) => s.btcUsd)
	return (
		<span className="flex items-center gap-1 tabular-nums text-foreground">
			<BitcoinIcon className="size-3.5" aria-hidden="true" />
			{btcUsd !== null ? (
				`$${btcUsd.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
			) : (
				<span className="text-muted-foreground">—</span>
			)}
		</span>
	)
}

/// The app's bottom bar IS the site's footer (2026-08-10): same component,
/// same content and layout — BTC price, fee rate + gear (the shared
/// site-wide fee level, via the footer's own FeeSelector instance), indexed
/// heights, support/Learn, socials — re-tinted onto the app's canvas through
/// the chrome tokens `.alkanes-surface` pins. One addition: the DIESEL price
/// rides immediately after the BTC entry, same visual grammar.
///
/// The phone's compact strip is the bottom edge now — the thumb bar is gone,
/// so the ticker sits where the desktop footer sits, with the Buy/Sell door
/// floating just above it.
export default function StatusBar() {
	return (
		<React.Fragment>
			{/* Desktop: the site footer, re-tinted. terminal.tsx pads the
			    workspace (pb-8) so the footer manages no spacer of its own. */}
			<div className="hidden lg:contents">
				<FooterV2
					testId="alkanes-status-bar"
					spacer={false}
					className="hidden lg:flex"
					priceSlot={<DieselPrice />}
				/>
			</div>

			{/* Phone: the compact strip pinned to the bottom edge. */}
			<div
				data-testid="alkanes-status-strip"
				className="fixed inset-x-0 bottom-0 z-30 flex h-[calc(1.75rem+env(safe-area-inset-bottom))] items-center justify-between gap-3 border-t border-border/60 bg-background/95 px-4 pb-[env(safe-area-inset-bottom)] text-xxs backdrop-blur lg:hidden"
			>
				<span className="flex min-w-0 items-center gap-2.5 overflow-hidden">
					<BtcPriceCompact />
					<DieselPrice compact />
				</span>
				<span className="flex shrink-0 items-center gap-2">
					<FeeCluster />
					<HealthDot />
				</span>
			</div>
		</React.Fragment>
	)
}
